import { Controller } from 'stimulus'

/**
 * ClipboardController
 * Handles copying text to clipboard with visual feedback
 */
export default class extends Controller {
  static targets = ['toast', 'includeRecord', 'redirectRecord', 'includeRecordAll', 'copyAllBtn']

  connect() {
    // Nothing to do on connect
  }

  // Copy specific text to clipboard
  async copy(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const text = button.dataset.clipboardText

    if (!text) {
      console.error('No text to copy')
      return
    }

    await this.copyToClipboard(text)
    this.showFeedback(button)
  }

  // Copy all DNS instructions
  async copyAll(event) {
    event.preventDefault()

    let text = ''

    // Build the full text to copy
    if (this.hasIncludeRecordAllTarget && this.hasIncludeRecordTarget) {
      const name = this.includeRecordAllTarget.textContent.trim().replace(/\s*\(or @\)\s*/g, '')
      const value = this.includeRecordTarget.textContent.trim()

      text = `DNS Record for SPF:\n`
      text += `Name: ${name}\n`
      text += `Type: TXT\n`
      text += `Value: ${value}\n`
    }

    if (text) {
      await this.copyToClipboard(text)
      if (this.hasCopyAllBtnTarget) {
        this.showFeedback(this.copyAllBtnTarget)
      }
      this.showToast()
    }
  }

  // Core clipboard copy function
  async copyToClipboard(text) {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }

      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        return true
      } finally {
        textArea.remove()
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      return false
    }
  }

  // Show visual feedback on the button
  showFeedback(button) {
    const originalText = button.innerHTML
    const originalClass = button.className

    // Change button appearance
    button.innerHTML = `
      <svg class="-ml-0.5 mr-1 h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
      </svg>
      Copied!
    `
    button.classList.add('text-green-700', 'bg-green-100')

    // Reset after delay
    setTimeout(() => {
      button.innerHTML = originalText
      button.className = originalClass
    }, 2000)
  }

  // Show toast notification
  showToast() {
    if (this.hasToastTarget) {
      this.toastTarget.classList.remove('hidden')

      setTimeout(() => {
        this.toastTarget.classList.add('hidden')
      }, 3000)
    } else {
      // Create a toast if target doesn't exist
      const toast = document.createElement('div')
      toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center'
      toast.innerHTML = `
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
        Copied to clipboard!
      `

      document.body.appendChild(toast)

      setTimeout(() => {
        toast.remove()
      }, 3000)
    }
  }
}
